import React from 'react'
import { SelectCard } from 'src/Components/Ui/SelectCard'
import { TabPanel } from 'src/Components/Ui/TabPanel'
import { State, PartialAction, Position } from 'src/types'
import { useOriginDispatcher } from 'src/hooks/useOriginDispatcher'
import { useDispatcher } from 'src/features/gorilla/logic/dispatchGenerator'
import {
  EDGE_COLOR_BUTTON_OPTION,
  HAMIDASHI_COLOR_BUTTON_OPTION,
  LABEL_BUTTON_OPTION,
  LACE_COLOR_BUTTON_OPTION,
  LEATHER_COLOR_BUTTON_OPTION,
  MOUTON_BUTTON_OPTION,
  STITCH_COLOR_BUTTON_OPTION,
  edgeColors,
  hamidashiObjs,
  laceColorObjs,
  moutonObjs,
  partsObjs,
  stitchColorObjs
} from 'src/features/gorilla/constants/color'
import {
  genColorObjects,
  dispatchColorObjectsGenerator,
  partsGenerator,
  gorillaColorFlagGenerator,
  colorCautionGenerator,
  gorillaLabelCautionGenerator,
  labelObjectsSwitcher
} from 'src/features/gorilla/logic/colorSetting'
import { DISPATCHER } from 'src/features/gorilla/constants/actionType'
import { SelectCardWithImage } from 'src/Components/Ui/SelectCardWithImage'

type Props = {
  state: State
  selectedIndex: number
  figurePanelNum: number
  position: Position
  dispatch: React.Dispatch<PartialAction>
}

export const ColorSetter: React.FC<Props> = ({ state, selectedIndex, figurePanelNum, position, dispatch }) => {
  const { series, parts, lace, edge, stitch, hamidashi, mouton, gorillaLabel, baseModel } = state
  const partsKey = parts?.value
  const { isSelectable, isLaceParts, isLabelParts } = gorillaColorFlagGenerator(position, series, partsKey)
  const dispatchObjects = dispatchColorObjectsGenerator(position, series, partsKey, state.baseModel?.productNumber)
  const handle = {
    [partsKey]: useOriginDispatcher(DISPATCHER, partsKey, dispatch, dispatchObjects),
    parts: useOriginDispatcher(DISPATCHER, 'parts', dispatch, partsObjs),
    lace: useDispatcher('lace', dispatch),
    edge: useDispatcher('edge', dispatch),
    stitch: useDispatcher('stitch', dispatch),
    hamidashi: useDispatcher('hamidashi', dispatch),
    mouton: useDispatcher('mouton', dispatch),
    gorillaLabel: useDispatcher('gorillaLabel', dispatch)
  }

  const colorObjects = genColorObjects(position, state)
  const partsObjects = partsGenerator(position, series, partsKey, figurePanelNum, baseModel.productNumber)
  if (!colorObjects || !partsObjects) return <></>
  return (
    <TabPanel selectedIndex={selectedIndex} index={1}>
      <SelectCard summary={'パーツ'} selectedLabel={parts?.label} objects={partsObjects} handleChange={handle.parts} isDisplay={isSelectable} />
      <SelectCard
        summary={`${parts?.label}カラー`}
        selectedLabel={colorObjects[partsKey]?.label}
        selectedColor={colorObjects[partsKey]?.color}
        objects={dispatchObjects}
        caution={colorCautionGenerator(isLaceParts, 'lace')}
        handleChange={handle[partsKey]}
        isDisplay={isSelectable}
        isError={colorObjects[partsKey]?.value === 'unselected'}
        className={LEATHER_COLOR_BUTTON_OPTION}
      />
      <SelectCard
        summary={'ヘリ革'}
        selectedLabel={edge.label}
        selectedColor={edge.color}
        objects={edgeColors}
        handleChange={handle.edge}
        isDisplay={isSelectable}
        isError={edge?.value === 'unselected'}
        defaultExpanded={false}
        className={EDGE_COLOR_BUTTON_OPTION}
      />
      <SelectCard
        summary={'レース'}
        selectedLabel={lace?.label}
        selectedColor={lace?.color}
        objects={laceColorObjs}
        handleChange={handle.lace}
        isDisplay={isSelectable}
        isError={lace?.value === 'unselected'}
        defaultExpanded={false}
        className={LACE_COLOR_BUTTON_OPTION}
      />
      <SelectCard
        summary={'ステッチ'}
        selectedLabel={stitch?.label}
        selectedColor={stitch?.color}
        objects={stitchColorObjs}
        handleChange={handle.stitch}
        isDisplay={isSelectable}
        isError={stitch?.value === 'unselected'}
        defaultExpanded={false}
        className={STITCH_COLOR_BUTTON_OPTION}
      />
      <SelectCard
        summary={'ハミダシ'}
        selectedLabel={hamidashi?.label}
        selectedColor={hamidashi?.color}
        objects={hamidashiObjs}
        handleChange={handle.hamidashi}
        isDisplay={isSelectable}
        isError={hamidashi?.value === 'unselected'}
        defaultExpanded={false}
        className={HAMIDASHI_COLOR_BUTTON_OPTION}
      />
      <SelectCardWithImage
        summary={'ムートン'}
        selectedLabel={mouton?.label}
        selectedColor={mouton?.color}
        objects={moutonObjs}
        handleChange={handle.mouton}
        isDisplay={isSelectable}
        isError={mouton?.value === 'unselected'}
        isShowLabel
        imageHeight={100}
        className={MOUTON_BUTTON_OPTION}
      />
      <SelectCardWithImage
        summary={'ラベル'}
        selectedLabel={gorillaLabel?.label}
        objects={labelObjectsSwitcher(position, series?.value)}
        handleChange={handle.gorillaLabel}
        isDisplay={isSelectable}
        isError={gorillaLabel?.value === 'unselected'}
        caution={gorillaLabelCautionGenerator(isLabelParts, colorObjects[partsKey]?.value, position)}
        imageWidth={160}
        isShowLabel
        className={LABEL_BUTTON_OPTION}
      />
    </TabPanel>
  )
}
